import { useEffect, useMemo } from 'react';
import { useReactiveVar } from '@apollo/client';
import { shoppingCartOffersVar, offerSelectedClientsVar } from '../../apollo/reactive-variables/shopping-cart';
import { OFFER_SHOPPING_CART } from '../../graphql';
import useDebouncedQuery from '../useDebouncedQuery';

const useOfferShoppingCart = () => {
  const shoppingCartOffers = useReactiveVar(shoppingCartOffersVar);

  const updatedShoppingCartOffers = useMemo(() => shoppingCartOffers.map((offer) => {
    const filteredOffer = {
      invoiceId: offer.invoiceId,
      dateToPay: offer.dateToPay
        ? offer.dateToPay.format('YYYY-MM-DD')
        : null,
    };

    return filteredOffer;
  }), [shoppingCartOffers]);

  const { data: shoppingCartData, loading } = useDebouncedQuery(
    OFFER_SHOPPING_CART,
    {
      offerSelected: updatedShoppingCartOffers.map(({ receiver, ...offer }) => offer),
    },
    500,
  );

  const clientOffers = useMemo(() => {
    if (shoppingCartData?.offerShoppingCart?.clientsInvoicesMapping) {
      return shoppingCartData.offerShoppingCart.clientsInvoicesMapping.reduce((acc, client) => {
        acc[client.clientId] = client.invoiceIds;
        return acc;
      }, {});
    }
    return {};
  }, [shoppingCartData?.offerShoppingCart?.clientsInvoicesMapping]);

  useEffect(() => {
    if (shoppingCartData?.offerShoppingCart && !loading) {
      offerSelectedClientsVar(clientOffers);
    }
  }, [shoppingCartData?.offerShoppingCart, loading, clientOffers]);

  const noOffers = useMemo(
    () => shoppingCartOffers.length === 0,
    [shoppingCartOffers],
  );

  const someOfferWithoutDateToPay = useMemo(
    () => shoppingCartOffers.some((offer) => offer.dateToPay === null),
    [shoppingCartOffers],
  );

  const disabledShowCart = useMemo(
    () => (noOffers || someOfferWithoutDateToPay) && false,
    [noOffers, someOfferWithoutDateToPay],
  );

  return {
    shoppingCartData: shoppingCartData?.offerShoppingCart,
    disabledShowCart,
    loading,
  };
};

export default useOfferShoppingCart;
